<script setup lang="ts">
import type { Draggable } from "@dnd-kit/dom";
import Hammer from "hammerjs";
import { computed, nextTick, onBeforeUnmount, onMounted, reactive, ref, watch } from "vue";
import { getLegacyApp } from "../../core/legacy-bridge";
import { productLibrary } from "../../data";
import { dragDropHandler } from "../../handlers";
import { productSelection } from "./productSelection";

const navList = ref<HTMLElement>();
const itemList = ref<HTMLElement>();

// Upholstery categories are the tabs; the selected tab's products are the items.
// Re-read whenever productSelection.version changes (productLibrary itself is not reactive).
const view = computed(() => {
    productSelection.version;
    // Empty until the products of a series have been loaded
    const categories = (productLibrary.productsCategories ?? []).filter(c => productLibrary.getProductCategory(c.type) === "UPHOLSTRY");
    const selected = categories.find(c => c.type === productSelection.categoryType) ?? categories[0];
    return { categories, selectedType: selected?.type, products: selected?.products ?? [] };
});

function selectCategory(type: string) {
    productSelection.categoryType = type;
    productSelection.version++;
}

// Items fade in once their thumbnail has loaded; a missing thumbnail is replaced by a default image
const loaded = reactive(new Set<string>());
const fallbackThumbnail = "/assets/series/1/products/171/thumbnail";
function onThumbnailError(e: Event) {
    const img = e.target as HTMLImageElement;
    if (!img.src.endsWith(fallbackThumbnail)) img.src = fallbackThumbnail;
}

// Every product image is a drag source for the canvas. Recreated whenever the items change.
let draggables: Draggable[] = [];
function createDraggables() {
    draggables.forEach(d => d.destroy());
    draggables = [...itemList.value.querySelectorAll<HTMLElement>(".selector__items__item__image")]
        .map(el => dragDropHandler.createDraggable(el.dataset.productnumber, el));
}
watch(() => view.value.products, () => nextTick(createDraggables), { flush: "post" });

// With a mouse, dragging the tab bar or the item list scrolls it horizontally (touch scrolls natively)
const isTouchDevice = "ontouchstart" in document.documentElement;
function dragToScroll(element: HTMLElement) {
    let scrollLeft = 0;
    const gestures = new Hammer(element);
    gestures.get("pan").set({ direction: Hammer.DIRECTION_HORIZONTAL });
    gestures.on("panend", () => { scrollLeft = element.scrollLeft; });
    gestures.on("panmove", e => {
        if ((e.srcEvent as PointerEvent).buttons > 0) {
            element.scrollLeft = scrollLeft - e.deltaX;
        }
    });
    return gestures;
}
let gestures: HammerManager[] = [];
onMounted(() => {
    if (!isTouchDevice) {
        gestures = [dragToScroll(navList.value), dragToScroll(itemList.value)];
    } else {
        navList.value.style.touchAction = "auto";
        itemList.value.style.touchAction = "auto";
    }
    createDraggables();
});
onBeforeUnmount(() => {
    gestures.forEach(g => g.destroy());
    draggables.forEach(d => d.destroy());
});
</script>

<template>
    <div class="product-selector selector" :class="{ show: productSelection.version > 0 }">
        <div class="selector__wrapper">

            <div class="selector__tabs">
                <div class="selector__tabs__nav__wrapper">
                    <ul ref="navList" class="selector__tabs__nav">
                        <li v-for="category in view.categories" :key="category.type"
                            class="selector__tabs__navitem" :class="{ active: category.type === view.selectedType }"
                            data-type="category" :data-category="category.type" :data-name="category.name"
                            @click="selectCategory(category.type)" v-html="category.name"></li>
                    </ul>
                </div>
                <div class="selector__items__wrapper">
                    <ul ref="itemList" class="selector__items">
                        <!-- Clicking a product places it (first module) or starts placing it at a connector (app.ts) -->
                        <li v-for="product in view.products" :key="product.productNo"
                            class="selector__items__item product" :class="{ show: loaded.has(product.productNo) }"
                            data-type="product" :data-productnumber="product.productNo"
                            @click="getLegacyApp().onProductClicked(product.productNo, $event)">
                            <div class="selector__items__item__wrapper">
                                <div class="selector__items__item__image" :data-productnumber="product.productNo">
                                    <img :src="product.thumbnail" @load="loaded.add(product.productNo)" @error="onThumbnailError">
                                </div>
                                <div class="selector__items__item__name">
                                    <div v-html="product.name"></div>
                                </div>
                            </div>
                        </li>
                    </ul>
                </div>
            </div>

        </div>
    </div>
</template>
